import { c as _c } from "react/compiler-runtime";
import { type DateTimeFormatOptions, useFormatter, useTranslations } from 'next-intl';
import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from 'react';
import type { IntegrityActivity, IntegrityOutcome } from '@/components/market/resolution-integrity/resolution-integrity-types';
import { outcomeHueToVar, useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { useFormattedCurrency } from '@/lib/currency';
const ZERO: bigint = BigInt(0);
const TWO: bigint = BigInt(2);
const TEN: bigint = BigInt(10);
const PERCENT_X100: bigint = BigInt(10_000);
// 1, 2, 2.5, 5, 10 × 10^k as numerator/denominator pairs.
const NICE_STEPS: ReadonlyArray<readonly [bigint, bigint]> = [[BigInt(1), BigInt(1)], [BigInt(2), BigInt(1)], [BigInt(5), BigInt(2)], [BigInt(5), BigInt(1)], [BigInt(10), BigInt(1)]];
// Below $10 whole-dollar labels would collapse the half tick to $0 or $1.
const WHOLE_LABEL_MIN_D6: bigint = BigInt(10_000_000);
const MULTI_DAY_SECONDS: number = 2 * 86_400;
const TIME_ZONE = 'UTC';
// Keeps the tooltip inside the plot height on many-outcome markets.
const TOOLTIP_MAX_ROWS = 6;
interface Series {
  index: number;
  label: string;
  color: string;
}
interface Bin {
  startAt: string;
  amounts: bigint[];
  total: bigint;
}

/**
 * Stacked bars of gross volume per outcome per bin, scaled to a rounded axis
 * maximum. Hover, focus or tap shows a bin's per-outcome amounts in a tooltip
 * pinned to the top of the plot so it never covers the bar being inspected.
 */
export function TradingActivityChart(t0) {
  const $ = _c(57);
  const {
    activity,
    outcomes
  } = t0;
  const t = useTranslations("market.resolutionIntegrity");
  const intl = useFormatter();
  const {
    formatD6
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== outcomes) {
    t1 = outcomes.map(_temp);
    $[0] = outcomes;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const colors = useOutcomeColor(t1);
  const [expanded, setExpanded] = useState(null);
  const plotRef = useRef(null);
  useCollapseOnOutsidePointer(plotRef, expanded !== null, setExpanded);
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== activity.binSeconds || $[3] !== activity.bins || $[4] !== activity.endAt || $[5] !== activity.startAt || $[6] !== colors || $[7] !== expanded || $[8] !== formatD6 || $[9] !== intl || $[10] !== outcomes || $[11] !== t) {
    let t10;
    if ($[20] !== colors) {
      t10 = (outcome, position) => {
        const color = colors[position];
        return color === undefined ? [] : [{
          index: outcome.index,
          label: outcome.label,
          color: outcomeHueToVar(color.hue, "cta-foreground")
        }];
      };
      $[20] = colors;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    const series = outcomes.flatMap(t10);
    const bins = activity.bins.map(toBin);
    const axisMax = niceCeiling(bins.reduce(_temp2, ZERO));
    const axisMode = axisMax >= WHOLE_LABEL_MIN_D6 ? "compactWhole" : "compact";
    let t11;
    if ($[22] !== formatD6) {
      t11 = value => formatD6(value.toString(), "compactTight");
      $[22] = formatD6;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    const amount = t11;
    const timeFormat = Date.parse(activity.endAt) - Date.parse(activity.startAt) >= MULTI_DAY_SECONDS * 1000 ? {
      month: "short",
      day: "numeric",
      timeZone: TIME_ZONE
    } : {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
      timeZone: TIME_ZONE
    };
    const time = ms => intl.dateTime(new Date(ms), timeFormat);
    const binRange = bin_0 => {
      const start = Date.parse(bin_0.startAt);
      return `${time(start)}–${time(start + activity.binSeconds * 1000)}`;
    };
    const startMs = Date.parse(activity.startAt);
    const endMs = Date.parse(activity.endAt);
    if ($[24] !== t) {
      t7 = t("activityTitle");
      $[24] = t;
      $[25] = t7;
    } else {
      t7 = $[25];
    }
    t8 = "trading-activity-chart";
    let t12;
    if ($[26] !== axisMode || $[27] !== formatD6) {
      t12 = formatD6(ZERO.toString(), axisMode);
      $[26] = axisMode;
      $[27] = formatD6;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    let t13;
    if ($[29] !== t12) {
      t13 = <span>{t12}</span>;
      $[29] = t12;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    const t14 = <div className="flex w-7 shrink-0 flex-col sm:w-8.5 justify-between text-right text-micro leading-3 tabular-nums text-foreground-tertiary"><span>{formatD6(axisMax.toString(), axisMode)}</span><span>{formatD6((axisMax / TWO).toString(), axisMode)}</span>{t13}</div>;
    let t15;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <div aria-hidden={true} className="pointer-events-none absolute inset-0 flex flex-col justify-between"><i className="block w-full border-t border-dashed border-border-primary" /><i className="block w-full border-t border-dashed border-border-primary" /><i className="block w-full border-t border-border-primary" /></div>;
      $[31] = t15;
    } else {
      t15 = $[31];
    }
    const t16 = <div ref={plotRef} className="relative min-w-0 flex-1">{t15}<div className="relative flex size-full items-end gap-0.75 px-0.5 sm:gap-1.75 sm:px-1">{bins.map((bin_1, binIndex) => <button key={bin_1.startAt} type="button" data-testid="trading-activity-bar" aria-label={`${binRange(bin_1)}: ${amount(bin_1.total)}`} aria-expanded={expanded === binIndex} onClick={() => setExpanded(expanded === binIndex ? null : binIndex)} onKeyDown={event => {
          if (event.key !== "Escape" || expanded === null) {
            return;
          }
          event.stopPropagation();
          setExpanded(null);
        }} className="group flex h-full min-w-0 flex-1 cursor-pointer items-end focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground-brand"><span className="flex w-full flex-col-reverse overflow-hidden rounded-t-item opacity-[0.78] transition-opacity duration-fast motion-reduce:transition-none group-hover:opacity-100 group-focus-visible:opacity-100 group-aria-expanded:opacity-100" style={{
            height: percentOf(bin_1.total, axisMax)
          }}>{series.map(entry => <span key={entry.index} className="block w-full" style={{
              height: percentOf(bin_1.amounts[entry.index] ?? ZERO, bin_1.total),
              background: entry.color
            }} />)}</span><BinTooltip range={binRange(bin_1)} total={amount(bin_1.total)} {...tooltipRows(series, bin_1, amount)} otherLabel={count => t("activityOtherOutcomes", {
            count
          })} /></button>)}</div></div>;
    if ($[32] !== t14 || $[33] !== t16) {
      t9 = <div className="flex h-50 gap-2.5 sm:h-60">{t14}{t16}</div>;
      $[32] = t14;
      $[33] = t16;
      $[34] = t9;
    } else {
      t9 = $[34];
    }
    t4 = "mt-2 ml-9.5 flex sm:ml-11 justify-between text-micro text-foreground-tertiary";
    const t17 = time(startMs);
    let t18;
    if ($[35] !== t) {
      t18 = t("axisOpened");
      $[35] = t;
      $[36] = t18;
    } else {
      t18 = $[36];
    }
    let t19;
    if ($[37] !== t18) {
      t19 = <small className="block text-micro text-foreground-secondary">{t18}</small>;
      $[37] = t18;
      $[38] = t19;
    } else {
      t19 = $[38];
    }
    t5 = <span>{t17}{t19}</span>;
    t6 = <span>{time(startMs + (endMs - startMs) / 2)}</span>;
    t2 = "text-right";
    t3 = time(endMs);
    $[2] = activity.binSeconds;
    $[3] = activity.bins;
    $[4] = activity.endAt;
    $[5] = activity.startAt;
    $[6] = colors;
    $[7] = expanded;
    $[8] = formatD6;
    $[9] = intl;
    $[10] = outcomes;
    $[11] = t;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t10;
  if ($[39] !== t) {
    t10 = t("axisResolved");
    $[39] = t;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== t10) {
    t11 = <small className="block text-micro text-foreground-secondary">{t10}</small>;
    $[41] = t10;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  let t12;
  if ($[43] !== t11 || $[44] !== t2 || $[45] !== t3) {
    t12 = <span className={t2}>{t3}{t11}</span>;
    $[43] = t11;
    $[44] = t2;
    $[45] = t3;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== t12 || $[48] !== t4 || $[49] !== t5 || $[50] !== t6) {
    t13 = <div className={t4}>{t5}{t6}{t12}</div>;
    $[47] = t12;
    $[48] = t4;
    $[49] = t5;
    $[50] = t6;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  let t14;
  if ($[52] !== t13 || $[53] !== t7 || $[54] !== t8 || $[55] !== t9) {
    t14 = <section aria-label={t7} data-testid={t8}>{t9}{t13}</section>;
    $[52] = t13;
    $[53] = t7;
    $[54] = t8;
    $[55] = t9;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  return t14;
}
function _temp2(acc, bin) {
  return bin.total > acc ? bin.total : acc;
}
function _temp(o) {
  return {
    title: o.label,
    index: o.index
  };
}
interface TooltipRows {
  rows: Array<Series & {
    value: string;
  }>;
  other: {
    count: number;
    value: string;
  } | null;
}

/** Outcomes that traded in the bin, in outcome order; the overflow folds into one row. */
function tooltipRows(series: ReadonlyArray<Series>, bin: Bin, amount: (value: bigint) => string): TooltipRows {
  const traded = series.filter(entry => (bin.amounts[entry.index] ?? ZERO) > ZERO);
  const shown = traded.length > TOOLTIP_MAX_ROWS ? traded.slice(0, TOOLTIP_MAX_ROWS - 1) : traded;
  const rest = traded.slice(shown.length);
  const restTotal = rest.reduce((sum, entry) => sum + (bin.amounts[entry.index] ?? ZERO), ZERO);
  return {
    rows: shown.map(entry => ({
      ...entry,
      value: amount(bin.amounts[entry.index] ?? ZERO)
    })),
    other: rest.length === 0 ? null : {
      count: rest.length,
      value: amount(restTotal)
    }
  };
}
function BinTooltip(t0) {
  const $ = _c(14);
  const {
    range,
    total,
    rows,
    other,
    otherLabel
  } = t0;
  let t1;
  if ($[0] !== total) {
    t1 = <b className="font-medium tabular-nums">{total}</b>;
    $[0] = total;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== range || $[3] !== t1) {
    t2 = <strong className="mb-2 flex justify-between border-b border-border-primary pb-2 font-medium text-foreground-primary">{range}{t1}</strong>;
    $[2] = range;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== rows) {
    t3 = rows.map(_temp3);
    $[5] = rows;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== other || $[8] !== otherLabel) {
    t4 = other === null ? null : <span className="mt-1.5 flex items-center gap-1.5"><span className="min-w-0 truncate">{otherLabel(other.count)}</span><b className="ml-auto font-medium tabular-nums">{other.value}</b></span>;
    $[7] = other;
    $[8] = otherLabel;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <span role="tooltip" className="pointer-events-none absolute top-3 left-1/2 z-10 hidden w-50 -translate-x-1/2 sm:w-57 rounded-panel-row border border-border-primary bg-background-tertiary p-3 text-left text-caption text-foreground-secondary shadow-elevation-popover group-hover:block group-focus-visible:block group-aria-expanded:block">{t2}{t3}{t4}</span>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function _temp3(row) {
  return <span key={row.index} className="mt-1.5 flex items-center gap-1.5"><i aria-hidden={true} className="inline-block size-1.5 shrink-0 rounded-pill" style={{
      background: row.color
    }} /><span className="min-w-0 truncate">{row.label}</span><b className="ml-auto font-medium tabular-nums">{row.value}</b></span>;
}
function useCollapseOnOutsidePointer(ref, active, setExpanded) {
  const $ = _c(5);
  let t0;
  let t1;
  if ($[0] !== active || $[1] !== ref || $[2] !== setExpanded) {
    t0 = () => {
      if (!active) {
        return;
      }
      const onPointerDown = event => {
        if (event.target instanceof Node && ref.current?.contains(event.target)) {
          return;
        }
        setExpanded(null);
      };
      document.addEventListener("pointerdown", onPointerDown);
      return () => document.removeEventListener("pointerdown", onPointerDown);
    };
    t1 = [ref, active, setExpanded];
    $[0] = active;
    $[1] = ref;
    $[2] = setExpanded;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
}
function toBin(bin: IntegrityActivity['bins'][number]): Bin {
  const amounts = bin.volumeD6ByOutcome.map(v => BigInt(v));
  return {
    startAt: bin.startAt,
    amounts,
    total: amounts.reduce((a, b) => a + b, ZERO)
  };
}

/** Smallest 1/2/2.5/5 × 10^k at or above `max` (D6), so axis ticks read as round amounts. */
function niceCeiling(max: bigint): bigint {
  if (max <= ZERO) return ZERO;
  let magnitude = BigInt(1);
  while (magnitude * TEN <= max) magnitude *= TEN;
  for (const [numerator, denominator] of NICE_STEPS) {
    const candidate = magnitude * numerator / denominator;
    if (candidate >= max) return candidate;
  }
  return magnitude * TEN;
}

/** CSS geometry only: integer ratio to two decimals of a percent. */
function percentOf(part: bigint, whole: bigint): string {
  if (whole === ZERO) return '0%';
  return `${String(Number(part * PERCENT_X100 / whole) / 100)}%`;
}